import { useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import Logo from '../../../components/Logo/Logo';
import { getPasswordErrors } from '../../../utils/validation';
import PasswordRequirements from '../../../components/common/PasswordRequirements';
import { registrationService } from '../../../services/api/registrationService';
import { useAppSelector } from '../../../store/index';
import { setUserId,setAuthToken } from '../../../redux/slices/userProfileSlice';
import { useDispatch } from 'react-redux';
import { setAuthenticated } from '../../../store/reducers/authSlice';
import { useNavigate } from 'react-router-dom';
import { saveSensitiveData } from '../../../utils/secureStorage';

interface CreatePasswordProps {
  onContinue: () => void;
  onBack: () => void;
}

export default function CreatePassword({
  onContinue,
  onBack,
}: CreatePasswordProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useAppSelector((state) => state.userProfile.token);
  const [password, setPassword] = useState('');

  const [confirmPassword, setConfirmPassword] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const passwordErrors = getPasswordErrors(password);
  const passwordsMatch = password === confirmPassword;
  const isButtonDisabled =
    !password ||
    !confirmPassword ||
    passwordErrors.length > 0 ||
    !passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);

    if (!isButtonDisabled) {
      try {
        const validateResult = await registrationService.setPassword(
          token,
          password,
          confirmPassword
        );

        if (!validateResult.success) {
          const errorMessage =
            validateResult.errors && validateResult.errors.length > 0
              ? validateResult.errors[0]
              : validateResult.message;
          return;
        }
        setSuccessMessage('Password set successfully!');
        await dispatch(setUserId(validateResult.data?.userId || ''));
        await dispatch(setAuthToken(validateResult.data?.token || ''));
        await saveSensitiveData('authToken', validateResult.data?.token || '');
        await dispatch(setAuthenticated(true));
        navigate('/personal-details');
        setTimeout(() => {
          onContinue();
        }, 2000);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.[0] ||
          error.message ||
          'An error occurred. Please try again.';
        setErrorList(errorMessage);
      }
    }
  };

  return (
    <div className="h-screen md:h-auto bg-[#F5F5F5] flex flex-col items-center px-6 pt-4 pb-12 relative">
      <button
        onClick={onBack}
        className="absolute left-6 top-4 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center z-10"
        aria-label="Go back"
      >
        <ArrowLeft className="w-6 h-6 text-brand-primary" />
      </button>

      <div className="w-full max-w-xl flex flex-col items-center flex-1">
        <div className="mb-0">
          <Logo />
        </div>

        <h1 className="text-center mb-8 text-[26px] md:text-3xl lg:text-4xl font-semibold tracking-wide">Create Password</h1>
        {errorList.length > 0 && (
          <div className="w-full mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            {errorList.map((err, i) => (
              <p key={i} className="text-sm text-red-600">
                â€¢ {err}
              </p>
            ))}
          </div>
        )}

        {successMessage && (
          <div className="w-full mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600 text-center">
              {successMessage}
            </p>
          </div>
        )}
        <div className="w-full space-y-6">
          <div>
            <div
              className={`rounded-lg relative ${
                focusedField === 'password' || password
                  ? 'bg-gradient-to-r from-brand-primary to-brand-gray p-[2px]'
                  : ''
              }`}
            >
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => {
                  setFocusedField(null);
                  if (password) setShowErrors(true);
                }}
                className={`w-full text-center bg-white h-12 md:h-14 placeholder:text-gray-500 text-base md:text-lg lg:text-xl pr-12 ${
                  focusedField === 'password' || password
                    ? 'border-0 rounded-[6px]'
                    : 'border-2 border-gray-300 rounded-lg'
                }`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors z-10"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            {showErrors && password && passwordErrors.length > 0 && (
              <div className="mt-2 space-y-1">
                {passwordErrors.map((error, index) => (
                  <p key={index} className="text-xs text-brand-primary text-left">
                    • {error}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div>
            <div
              className={`rounded-lg relative ${
                focusedField === 'confirmPassword' || confirmPassword
                  ? 'bg-gradient-to-r from-brand-primary to-brand-gray p-[2px]'
                  : ''
              }`}
            >
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                className={`w-full text-center bg-white h-12 md:h-14 placeholder:text-gray-500 text-base md:text-lg lg:text-xl pr-12 ${
                  focusedField === 'confirmPassword' || confirmPassword
                    ? 'border-0 rounded-[6px]'
                    : 'border-2 border-gray-300 rounded-lg'
                }`}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors z-10"
                aria-label={
                  showConfirmPassword ? 'Hide password' : 'Show password'
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            {showErrors && confirmPassword && !passwordsMatch && (
              <p className="mt-2 text-xs text-brand-primary text-left">
                • Passwords do not match
              </p>
            )}
          </div>
        </div>

        <PasswordRequirements password={password} className="w-full mt-6" />
      </div>

      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm flex flex-col flex-1"
      >
        <div className="mt-auto pt-10 pb-4 w-full">
          <Button
            type="submit"
            disabled={isButtonDisabled}
            className="w-full bg-brand-primary hover:bg-brand-hover text-white h-12 md:h-14 lg:h-16 text-lg md:text-xl lg:text-2xl font-medium rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed text-center"
          >
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
}
